/**
 * Pure helpers for the shared <PromptEditor scope> component (PR-E).
 *
 * - `EMPTY_DRAFT` — initial draft shape used when opening "New prompt".
 * - `buildCreateBody` / `buildUpdateBody` — payload serializers used by
 *   the adapters (one variant per scope; sysadmin adds visibleToTenant).
 * - `normalizeVariablesFromHelper` — coerces the loose `{name, label, ...}`
 *   args coming back from the Prompt Helper LLM into the
 *   PromptVariableEntry shape.
 * - `rowScope` — tri-state classifier for tenant rows (system / cloned /
 *   tenant) used by the scope filter chips.
 *
 * Per project pattern (see agent-new-shared/AgentNew.helpers.ts header):
 * pure logic only; tests assert on these without any DOM / RTL.
 */
import type { PromptVariableEntry } from '@/components/studio';
import type { PromptDraft, NormalizedPrompt } from './PromptEditor.types';

// ─── Initial draft (shared shape; sysadmin adds visibleToTenant) ───────────

export const EMPTY_DRAFT: PromptDraft = {
  command: '',
  title: '',
  description: '',
  content: '',
  variables: [],
  category: 'general',
};

export const EMPTY_SYSADMIN_DRAFT: PromptDraft = {
  ...EMPTY_DRAFT,
  visibleToTenant: false,
};

// ─── Tri-state scope classifier (tenant chips) ─────────────────────────────

/**
 * Tenant-only: classify a prompt row into one of three buckets.
 *
 *   system  — sysadmin-authored row visible to this tenant (read-only)
 *   cloned  — tenant-owned row that originated from a clone-from-system
 *   tenant  — tenant-owned original row
 */
export function rowScope(
  p: Pick<NormalizedPrompt, 'isSystem' | 'clonedFromSystemId'>,
): 'system' | 'cloned' | 'tenant' {
  if (p.isSystem) return 'system';
  if (p.clonedFromSystemId) return 'cloned';
  return 'tenant';
}

// ─── Tenant CREATE body ────────────────────────────────────────────────────

/**
 * Build the tenant /templates POST body from a draft.
 * Description is omitted when blank — preserves the pre-refactor server
 * behaviour that distinguishes "blank string" from "null".
 */
export function buildTenantCreateBody(draft: PromptDraft): {
  command: string;
  title: string;
  description?: string;
  content: string;
  variables: PromptVariableEntry[];
  category: string;
} {
  return {
    command: draft.command,
    title: draft.title,
    description: draft.description || undefined,
    content: draft.content,
    variables: draft.variables,
    category: draft.category,
  };
}

// ─── Tenant UPDATE body ────────────────────────────────────────────────────

/**
 * Build the tenant /templates/:id PUT body from a draft.
 * `command` is intentionally omitted — slug is immutable post-create.
 */
export function buildTenantUpdateBody(draft: PromptDraft): {
  title: string;
  description?: string;
  content: string;
  variables: PromptVariableEntry[];
  category: string;
} {
  return {
    title: draft.title,
    description: draft.description || undefined,
    content: draft.content,
    variables: draft.variables,
    category: draft.category,
  };
}

// ─── Sysadmin CREATE body ──────────────────────────────────────────────────

/**
 * Build the sysadmin /admin/platform/studio-prompts POST body from a draft.
 * Adds `visibleToTenant` (Bucket C invariant).
 */
export function buildSysadminCreateBody(draft: PromptDraft): {
  command: string;
  title: string;
  description?: string;
  content: string;
  variables: PromptVariableEntry[];
  category: string;
  visibleToTenant: boolean;
} {
  return {
    command: draft.command,
    title: draft.title,
    description: draft.description || undefined,
    content: draft.content,
    variables: draft.variables,
    category: draft.category,
    visibleToTenant: !!draft.visibleToTenant,
  };
}

// ─── Sysadmin UPDATE body ──────────────────────────────────────────────────

/**
 * Build the sysadmin /admin/platform/studio-prompts/:id PATCH body from
 * a draft. Adds `visibleToTenant` (Bucket C invariant). Slug omitted
 * (immutable post-create).
 */
export function buildSysadminUpdateBody(draft: PromptDraft): {
  title: string;
  description?: string;
  content: string;
  variables: PromptVariableEntry[];
  category: string;
  visibleToTenant: boolean;
} {
  return {
    title: draft.title,
    description: draft.description || undefined,
    content: draft.content,
    variables: draft.variables,
    category: draft.category,
    visibleToTenant: !!draft.visibleToTenant,
  };
}

// ─── Variable coercion (Prompt Helper → PromptVariableEntry[]) ─────────────

/**
 * Coerce the loose array shape coming back from the Prompt Helper LLM
 * (via the `set_variables` tool call) into the strict PromptVariableEntry
 * shape the editor stores. Falls back to safe defaults for missing
 * fields; the canonical shape and accepted types are defined inside
 * `@/components/studio/variable-editor`.
 *
 * Tenant adapter only — sysadmin route never wired the Prompt Helper.
 */
export function normalizeVariablesFromHelper(
  vars: unknown,
): PromptVariableEntry[] {
  if (!Array.isArray(vars)) return [];
  return vars.map((raw): PromptVariableEntry => {
    const v = (raw ?? {}) as Record<string, unknown>;
    const type =
      v.type === 'select' ||
      v.type === 'number' ||
      v.type === 'date' ||
      v.type === 'textarea' ||
      v.type === 'boolean'
        ? v.type
        : 'text';
    const name = String(v.name ?? '');
    return {
      name,
      label: String(v.label ?? name),
      type,
      required: !!v.required,
      default: v.default != null ? String(v.default) : undefined,
      options: Array.isArray(v.options) ? (v.options as unknown[]).map(String) : undefined,
    };
  });
}

// ─── Helper-mutation → draft patch (#1094 fix) ─────────────────────────────

/**
 * Apply one Prompt Helper mutation tool call to the in-sheet draft.
 *
 * #1094 — Pre-fix the Accept handler called `setDraft({ ...draft, command })`,
 * then `setDraft({ ...draft, title })`, etc. in sequence. Each `setDraft`
 * captured the SAME stale `draft` from the React closure, so only the
 * last call's field landed and every other field stayed blank. The
 * symptom was: helper streams a `set_metadata` card with command/title/
 * description/category all set, user clicks Accept, the card disappears
 * and the only one of the four fields that ends up populated is whichever
 * one happened to be last in the `if`-chain. With every field empty the
 * "Create prompt" button stays disabled, so the helper appears to do
 * nothing.
 *
 * The fix is to compute ALL field updates into one object and let the
 * caller pass it to `setDraft(prev => prev ? { ...prev, ...patch } : prev)`
 * (functional setState — never reads stale closure).
 *
 * Returns a partial patch. Unknown tool names → empty patch (no-op).
 *
 * @param toolName  - one of `set_metadata` | `set_template_body` | `set_variables`
 * @param args      - the helper's tool args (validated server-side via Zod)
 */
export function applyPromptHelperMutation(
  toolName: string,
  args: Record<string, unknown>,
): Partial<PromptDraft> {
  switch (toolName) {
    case 'set_metadata': {
      const patch: Partial<PromptDraft> = {};
      if (args.command != null) patch.command = String(args.command);
      if (args.displayName != null) patch.title = String(args.displayName);
      if (args.description != null) patch.description = String(args.description);
      if (args.category != null) patch.category = String(args.category);
      return patch;
    }
    case 'set_template_body': {
      if (args.templateBody != null) {
        return { content: String(args.templateBody) };
      }
      return {};
    }
    case 'set_variables': {
      if (Array.isArray(args.variables)) {
        return { variables: normalizeVariablesFromHelper(args.variables) };
      }
      return {};
    }
    default:
      return {};
  }
}
